import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { calculateTimeLeft, validateCoordinates } from '@nx-smartmonkey/shared/helpers';
import { FixedMarkerMap, LatLng, RoutalLoader, VSpacer } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PlannerOnboardingImage from '../../../../assets/img/planner_onboarding_image.png';
import { FullPageContainer } from '../../../../components/FullPageContainer';
import { LoaderPage } from '../../../../components/LoaderPage';
import { Modal } from '../../../../components/Modal';
import { ROUTE_STATUS } from '../../../../constants/Routes';
import * as routing from '../../../../constants/Routing';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Stop } from '../../../../domain/stops/Stop';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useRetrieveActiveLanguage } from '../../../../hooks/translations/useRetrieveActiveLanguage';
import { getLocationFromCoordinates } from '../../../../services/requests';
import { FREE_STOP_LIMIT } from '../../components/upgradeSubscription/UpgradeSubscriptionComponent';
import { usePermissions } from '../../hooks/usePermissions';
import { useRetrieveDriverCredentials } from '../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { useUpgradeDialogVisibility } from '../../hooks/useUpgradeDialogVisibility';
import { useEndUnfinishedRoutes } from '../../routes-list/hooks/useEndUnfinishedRoutes';
import { useSearchUnfinishedRoutes } from '../../routes-list/hooks/useSearchUnfinishedRoutes';
import { useEndRoute } from '../hooks/useEndRoute';
import { useReopenRoute } from '../hooks/useReopenRoute';
import { useRetrieveRoute } from '../hooks/useRetrieveRoute';
import { useStartRoute } from '../hooks/useStartRoute';
import { RouteCompletedDialog } from '../route-complete/RouteCompletedDialog';
import { CreateStopsBulk } from './create-stops/bulk/CreateStopsBulk';
import { CreateStopsManually, createStop } from './create-stops/CreateStopsManually';
import { RoutesStopsSearchMenu } from './floating-menus/RoutesStopsSearchMenu';
import { ReorderRoute } from './reorder-route/ReorderRoute';
import { RouteFooter } from './route-stops-footers/RouteFooter';
import { RouteFooterContainer } from './route-stops-footers/RouteFooterContainer';
import { RouteStopsContainer } from './RouteStopsContainer';
import { RouteStopsMap } from './RouteStopsMap';
import { SelectedStop } from './stop';

export const RouteStops = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { routeId } = useParams();
  const [currentLocationPathName, setCurrentLocationPathName] = useState(location.pathname);
  const { data: vehicleData } = useRetrieveVehicle();
  const { data: credentials } = useRetrieveDriverCredentials();
  const {
    isLoading,
    error: retrieveRouteError,
    data: selectedRoute,
  } = useRetrieveRoute({ routeId: routeId as string });
  const { data: remainingRoutes } = useSearchUnfinishedRoutes();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const { isSoloDriver, hasSubscription, canManageStops } = usePermissions();
  const {
    run: startRoute,
    isLoading: startingRoute,
    success: startRouteSuccess,
  } = useStartRoute({
    driverRoute: selectedRoute!,
  });

  const {
    run: endRoute,
    isLoading: endingRoute,
    success: endRouteSuccess,
  } = useEndRoute({
    driverRoute: selectedRoute!,
  });

  const {
    run: reopenRoute,
    isLoading: reopeningRoute,
    success: reopenRouteSuccess,
  } = useReopenRoute({
    driverRoute: selectedRoute!,
  });
  const { setDialogVisibility } = useUpgradeDialogVisibility();
  const { run: endOpenRoutes, isLoading: unfinishedLoading, success: unfinishedSuccess } = useEndUnfinishedRoutes();
  const { canReorderStops } = usePermissions();
  const [customSearchParams, setCustomSearchParams] = useSearchParams();
  const [stops, setStops] = useState<Stop[]>([]);
  const [mapStop, setMapStop] = useState<Location | null>(null);
  const [isLoadingDialog, setIsLoadingDialog] = useState<boolean>(false);
  const [isSearchSliderVisible, setIsSearchSliderVisible] = useState<boolean>(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);
  const [allStopsCompleted, setAllStopsCompleted] = useState<boolean>(false);
  const [disabledClicks, setDisabledClicks] = useState<boolean>(false);
  const [dialog, setDialog] = useState<
    `reopenRoute` | `routeCompleted` | `openOldRoutes` | `create_stop_from_map` | null
  >(null);

  const updateRouteLoading = startingRoute || endingRoute || reopeningRoute;
  const showDialogCompleted = dialog === `routeCompleted`;

  const goToFirstStop = () => {
    const firstStop = (selectedRoute?.stops ?? [])[0];
    if (firstStop) {
      navigate(
        routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute!.id).replace(`:stopId`, firstStop.id)
      );
    }
  };

  // If location changes, reset the dialogs and menus
  useEffect(() => {
    if (location.pathname !== currentLocationPathName) {
      setCurrentLocationPathName(location.pathname);
      setDialog(null);
      setIsSearchSliderVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (selectedRoute?.status !== ROUTE_STATUS.inTransit) return;

    // To avoid iterating over the stops
    if (selectedRoute?.stops.some((stop) => stop.status === STOP_STATUS.pending)) {
      if (allStopsCompleted) setAllStopsCompleted(false);
    } else {
      if (!allStopsCompleted) setAllStopsCompleted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute?.id, selectedRoute?.status, selectedRoute?.stops]);

  useEffect(() => {
    if (retrieveRouteError) {
      navigate(routing.PRIVATE_ROUTES.ROOT);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrieveRouteError]);

  useEffect(() => {
    const onboardingModal = customSearchParams.getAll(`from_planner_onboarding`)[0] ?? false;
    setShowOnboardingModal(onboardingModal === `true`);
  }, [customSearchParams]);

  useEffect(() => {
    setDialog(null);
    if (unfinishedSuccess && selectedRoute?.status === ROUTE_STATUS.notStarted) {
      startRoute();
    }
    if (startRouteSuccess && selectedRoute?.status === ROUTE_STATUS.inTransit) {
      goToFirstStop();
    }
    if (endRouteSuccess && selectedRoute?.status === ROUTE_STATUS.finished) {
      setDialog(`routeCompleted`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unfinishedSuccess, startRouteSuccess, endRouteSuccess, reopenRouteSuccess]);

  useEffect(() => {
    if (selectedRoute && selectedRoute.stops.length > FREE_STOP_LIMIT && !hasSubscription && isSoloDriver) {
      setDialogVisibility(true);
      navigate(routing.PRIVATE_ROUTES.ROOT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute]);

  if (isLoading || !selectedRoute) return <LoaderPage />;

  if (!selectedRoute) return null;

  return (
    <FullPageContainer
      id="route-stops"
      style={{
        position: `relative`,
      }}
    >
      <RouteStopsMap
        selectedRoute={selectedRoute}
        handleOnSelectStop={(stop: Stop) => {
          //If we are in a route
          if (!updateRouteLoading && !disabledClicks) {
            navigate(
              routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(`:stopId`, stop.id)
            );
          }
        }}
        onLongClick={async (location: LatLng) => {
          if (canManageStops && selectedRoute.status !== `finished`) {
            setIsLoadingDialog(true);
            try {
              let newOptions: Location[] = [];
              if (location.lat && location.lng && validateCoordinates({ lat: location.lat, lng: location.lng })) {
                newOptions = await getLocationFromCoordinates({
                  coordinates: [
                    {
                      lat: location.lat,
                      lng: location.lng,
                    },
                  ],
                  lang: activeLanguage,
                });
              }
              if (newOptions[0]) {
                setMapStop(newOptions[0]);
                setDialog(`create_stop_from_map`);
              }
            } catch (error) {
              console.error(`Error Map AddLocation`, error);
            } finally {
              setIsLoadingDialog(false);
            }
          }
        }}
      />

      <RouteCompletedDialog open={showDialogCompleted} selectedRoute={selectedRoute} onClose={() => setDialog(null)} />

      <Modal
        open={dialog === `reopenRoute`}
        title={{
          intlId: `driver.reopen_warning.title`,
        }}
        subtitle={{
          intlId:
            calculateTimeLeft(selectedRoute.end_date) > 0
              ? `driver.reopen_warning.info`
              : `driver.reopen_warning.info_today`,
        }}
        buttons={[
          {
            intlId: `driver.reopen_warning.reopen`,
            onClick: () => {
              reopenRoute();
            },
            disabled: updateRouteLoading,
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setDialog(null);
            },
            disabled: updateRouteLoading,
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={<VSpacer medium />}
      />

      <Modal
        open={dialog === `openOldRoutes`}
        title={{
          intlId: `driver.pending_routes_warning.title_start`,
        }}
        subtitle={{
          intlId: `driver.pending_routes_warning.info_start`,
          intlValues: { remainingRoutes: remainingRoutes ?? 0 },
        }}
        isLoading={updateRouteLoading}
        buttons={[
          {
            intlId: `driver.pending_routes_warning.continue_with_pending_routes`,
            onClick: () => {
              if (updateRouteLoading) return;
              startRoute();
              setDialog(null);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
            isLoading: updateRouteLoading,
          },
          {
            intlId: `driver.pending_routes_warning.finish_all_routes`,
            onClick: () => {
              endOpenRoutes();
            },
            isLoading: unfinishedLoading || updateRouteLoading,
            disabled: updateRouteLoading && !unfinishedLoading,
          },
        ]}
        children={<VSpacer large />}
      />

      <Modal
        open={(dialog === `create_stop_from_map` && mapStop !== null) || isLoadingDialog}
        onClose={() => {
          setDialog(null);
        }}
        title={{
          intlId: `driver.stops.create_stops.add_stop_from_map_dialog.title`,
        }}
        subtitle={
          isLoadingDialog
            ? { intlId: `driver.stops.create_stops.add_stop_from_map_dialog.subtitle.loading` }
            : {
                text: intl.formatMessage(
                  {
                    id: `driver.stops.create_stops.add_stop_from_map_dialog.subtitle`,
                  },
                  { value: mapStop?.label }
                ),
              }
        }
        buttons={[
          {
            intlId: `accept`,
            disabled: isLoadingDialog,
            onClick: () => {
              if (mapStop)
                setStops([...stops, createStop(mapStop, vehicleData?.project.fields.stop_fields ?? [], stops.length)]);
              setDialog(null);
              setMapStop(null);
              navigate(
                `${routing.PRIVATE_ROUTES.CREATE_STOPS_MANUAL.replace(`:routeId`, selectedRoute.id)}?from_map=true`
              );
            },
          },
          {
            intlId: `cancel`,
            disabled: isLoadingDialog,
            onClick: () => {
              setDialog(null);
              setMapStop(null);
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
        ]}
        children={
          <>
            <Grid container direction="column" alignItems="center" justifyContent="center" style={{ height: `200px` }}>
              {isLoadingDialog || !mapStop ? (
                <RoutalLoader style={{ width: `40px`, height: `40px`, opacity: 1 }} />
              ) : (
                <FixedMarkerMap
                  markerPosition={[mapStop!.lat as number, mapStop!.lng as number]}
                  onPositionChange={_.debounce(async (latLng: LatLng) => {
                    try {
                      const newLocations = await getLocationFromCoordinates({
                        coordinates: [
                          {
                            lat: latLng.lat,
                            lng: latLng.lng,
                          },
                        ],
                        lang: activeLanguage,
                      });

                      if (newLocations[0]) {
                        setMapStop(newLocations[0]);
                      }
                    } catch (error) {
                      console.error(`Error Map AddLocation`, error);
                    }
                  }, 800)}
                  height={200}
                  mapStyles={{
                    borderRadius: `10px`,
                  }}
                />
              )}
            </Grid>

            <VSpacer medium />
          </>
        }
      />

      <Modal
        open={showOnboardingModal}
        title={{
          intlId: `drivers.planner_onboarding_dialog.title`,
        }}
        subtitle={{
          intlId: `drivers.planner_onboarding_dialog.subtitle`,
        }}
        buttons={[
          {
            intlId: `accept`,
            onClick: () => {
              setCustomSearchParams();
            },
          },
        ]}
        children={
          <>
            <VSpacer medium />
            <Grid container direction="column" justifyContent="center" alignItems="center">
              {<img style={{ width: `80%` }} src={PlannerOnboardingImage} alt="Planner Onboarding" />}
            </Grid>
            <VSpacer medium />
          </>
        }
      />

      <Routes>
        {!showDialogCompleted && (
          <Route
            path="/stops/:stopId/*"
            element={
              <SelectedStop
                allStopsCompleted={allStopsCompleted}
                selectedRoute={selectedRoute}
                setReopenRouteDialogOpen={() => setDialog(`reopenRoute`)}
                setDisableClicks={setDisabledClicks}
                externalLoading={updateRouteLoading}
                endRoute={() => {
                  endRoute();
                  navigate(routing.PRIVATE_ROUTES.ROUTE_DETAILS.replace(`:routeId`, selectedRoute.id));
                }}
              />
            }
          />
        )}

        {credentials && canReorderStops && selectedRoute.status !== `finished` && (
          <Route path="/reorder" element={<ReorderRoute selectedRoute={selectedRoute} />} />
        )}
        {canManageStops && selectedRoute.status !== `finished` && (
          <Route
            path="/create-stops/*"
            element={
              <Routes>
                <Route
                  path="/manual"
                  element={<CreateStopsManually selectedRoute={selectedRoute} stops={stops} setStops={setStops} />}
                />
                <Route path="/bulk" element={<CreateStopsBulk selectedRoute={selectedRoute} />} />
                <Route
                  path="*"
                  element={
                    <Navigate to={routing.PRIVATE_ROUTES.CREATE_STOPS_MANUAL.replace(`:routeId`, selectedRoute.id)} />
                  }
                />
              </Routes>
            }
          />
        )}

        <Route
          path="*"
          element={
            <>
              {!showDialogCompleted && (
                <>
                  <RoutesStopsSearchMenu
                    selectedRoute={selectedRoute}
                    isSliderVisible={isSearchSliderVisible}
                    setIsSliderVisible={setIsSearchSliderVisible}
                    onSelectStop={(stopId: string) => {
                      if (!updateRouteLoading && !disabledClicks) {
                        navigate(
                          routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(
                            `:stopId`,
                            stopId
                          )
                        );
                      }
                    }}
                  />
                  <RouteStopsContainer
                    openSearch={() => setIsSearchSliderVisible(true)}
                    footerSize={
                      selectedRoute.stops.length !== 0 &&
                      (selectedRoute.status === ROUTE_STATUS.notStarted ||
                        startingRoute ||
                        selectedRoute.status === ROUTE_STATUS.finished ||
                        (selectedRoute.status === ROUTE_STATUS.inTransit && allStopsCompleted))
                        ? 70
                        : 0
                    }
                    selectedRoute={selectedRoute}
                    onSelectStop={(stop: Stop) => {
                      if (!updateRouteLoading && !disabledClicks) {
                        navigate(
                          routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(
                            `:stopId`,
                            stop.id
                          )
                        );
                      }
                    }}
                    onEndRoute={() => {
                      endRoute();
                    }}
                  />
                  {selectedRoute.stops.length !== 0 &&
                  (selectedRoute.status === ROUTE_STATUS.notStarted || startingRoute) ? (
                    <RouteFooterContainer>
                      <RouteFooter
                        isLoading={updateRouteLoading || unfinishedLoading}
                        onClick={() => {
                          if (remainingRoutes) {
                            setDialog(`openOldRoutes`);
                          } else {
                            startRoute();
                          }
                        }}
                        intlId="driver.route.start_route"
                      />
                    </RouteFooterContainer>
                  ) : null}
                  {selectedRoute.status === ROUTE_STATUS.finished && !showDialogCompleted ? (
                    <RouteFooterContainer>
                      <RouteFooter
                        onClick={() => {
                          setDialog(`reopenRoute`);
                        }}
                        intlId="driver.route.reopen_route"
                        isLoading={updateRouteLoading}
                      />
                    </RouteFooterContainer>
                  ) : null}
                  {selectedRoute.status === ROUTE_STATUS.inTransit && allStopsCompleted ? (
                    <RouteFooterContainer>
                      <RouteFooter
                        isLoading={updateRouteLoading}
                        onClick={() => {
                          endRoute();
                        }}
                        intlId="driver.route.finish_route"
                      />
                    </RouteFooterContainer>
                  ) : null}
                </>
              )}
            </>
          }
        />
      </Routes>
    </FullPageContainer>
  );
};
